import { Button, Combobox, ComboboxProps, Field, tokens, useComboboxFilter } from "@fluentui/react-components";
import SyncIcon from "~icons/material-symbols/sync";
import _ from "lodash";
import { memo, useEffect, useMemo, useState } from "react";
import { Workspace } from "../../models/workspace";
import { LoadingContainer } from "../Loading";
import { WorkspaceOptionsList } from "./components/WorkspaceOptionsList";
import { useWorkspaceSelectorStyles } from "./styles";

export type WorkspaceSelectorProps = {
  selectedWorkspace?: string;
  onSelectWorkspace: (selectedWorkspace: string) => void;
  workspaces: Workspace[];
  isLoading?: boolean;
  refreshWorkspaces?: () => void;
} & ComboboxProps;

export const WorkspaceSelector = memo(
  ({
    selectedWorkspace,
    workspaces,
    isLoading,
    refreshWorkspaces,
    onSelectWorkspace,
    ...props
  }: WorkspaceSelectorProps) => {
    const styles = useWorkspaceSelectorStyles();

    const options = useMemo(() => {
      if (isLoading) return [];
      return workspaces
        .map((workspace: any) => ({
          children: workspace.name,
          value: `${workspace.workspaceId}`,
        }))
        .sort((a: any, b: any) => a.children - b.children);
    }, [workspaces, isLoading]);

    const onChangeWorkspaceText: ComboboxProps["onChange"] = (event) => {
      const value = event.target.value?.trim();
      setQuery(value as string);
    };

    const [query, setQuery] = useState<string>(() => {
      if (selectedWorkspace) {
        return (_.find(workspaces, (w) => w.workspaceId === selectedWorkspace.toString())?.name as string) || "";
      }
      return "";
    });
    const children = useComboboxFilter(query, options, {
      noOptionsMessage: "No results match your search.",
      optionToText: (option) => option.children,
    });

    const onFocus = () => {
      setQuery("");
    };

    const onBlur = () => {
      if (selectedWorkspace) {
        setQuery((_.find(workspaces, (w) => w.workspaceId === selectedWorkspace.toString())?.name as string) || "");
      }
    };

    useEffect(() => {
      if (!selectedWorkspace) return;
      setQuery((_.find(workspaces, (w) => w.workspaceId === selectedWorkspace.toString())?.name as string) || "");
    }, [selectedWorkspace]);

    return (
      <Field className={styles.container}>
        <Combobox
          onFocus={onFocus}
          onBlur={onBlur}
          positioning={{ matchTargetSize: "width" }}
          listbox={{ className: styles.listbox }}
          disabled={isLoading}
          multiselect={false}
          className={styles.combobox}
          placeholder="Select Workspaces"
          size={props.size}
          freeform={true}
          value={query}
          onChange={onChangeWorkspaceText}
          selectedOptions={selectedWorkspace ? [selectedWorkspace] : []}
          onOptionSelect={(_ev, data) => {
            data.optionText && setQuery(data.optionText);
            data.optionValue && onSelectWorkspace(data.optionValue);
          }}
          {...props}
        >
          <WorkspaceOptionsList options={children} />
        </Combobox>
        <LoadingContainer isLoading={!!isLoading} size="extra-tiny" className={styles.loaderContainer}>
          <Button
            size="small"
            appearance="transparent"
            icon={<SyncIcon color={tokens.colorBrandBackground} />}
            onClick={refreshWorkspaces}
          />
        </LoadingContainer>
      </Field>
    );
  }
);
